import { useEffect, useState } from "react";

import { Check, Copy, Eye, EyeOff, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useProvisionStaffMember } from "@/features/staff/hooks/useStaff";
import {
  STAFF_EMAIL_DOMAIN,
  buildStaffEmail,
  isValidStaffEmailLocalPart,
  normalizeStaffEmailLocalPart,
} from "@/features/staff/schemas/staff";
import { generateSecurePassword } from "@/features/staff/utils/generateSecurePassword";

interface StaffFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** Se llama con el uid recién creado, para asignarle roles a continuación. */
  onCreated?: (uid: string, email: string) => void;
}

/** Mínimo de Firebase Auth; por debajo rechaza la contraseña. */
const MIN_PASSWORD_LENGTH = 6;

/**
 * Alta de un miembro del equipo: crea su cuenta de acceso y su perfil.
 *
 * La cuenta nace SIN ningún rol. El acceso se concede después, asignándole
 * roles: entrar al panel y poder hacer algo dentro son dos decisiones distintas.
 */
export function StaffFormDialog({ open, onClose, onCreated }: StaffFormDialogProps) {
  const provision = useProvisionStaffMember();

  const [emailLocal, setEmailLocal] = useState("");
  const [name, setName] = useState("");
  const [position, setPosition] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) return;
    setEmailLocal("");
    setName("");
    setPosition("");
    setPassword("");
    setShowPassword(false);
    setCopied(false);
  }, [open]);

  const email = buildStaffEmail(emailLocal);
  const emailIsValid = isValidStaffEmailLocalPart(emailLocal);
  const passwordIsValid = password.length >= MIN_PASSWORD_LENGTH;
  const canSubmit =
    emailIsValid && passwordIsValid && name.trim().length > 0 && !provision.isPending;

  const handleGeneratePassword = () => {
    const next = generateSecurePassword();
    setPassword(next);
    setShowPassword(true);
    setCopied(false);
  };

  const handleCopyPassword = async () => {
    if (!password) return;
    await navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmit = async () => {
    try {
      const { uid, grantedAdminRole } = await provision.mutateAsync({
        email,
        password,
        name: name.trim(),
        position: position.trim(),
      });

      if (grantedAdminRole) {
        toast.success(
          `${email} es el primer miembro del equipo: recibe el rol Administrador para que puedas seguir gestionando el panel.`
        );
        onClose();
        return;
      }

      toast.success(`${email} añadido al equipo. Asígnale roles para darle acceso.`);
      onCreated?.(uid, email);
      onClose();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "No se pudo crear la cuenta"
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Nuevo miembro del equipo</DialogTitle>
          <DialogDescription>
            Se crea su cuenta de acceso y su perfil. Empieza sin ningún rol: tú decides
            después qué puede hacer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="staff-name">Nombre</Label>
            <Input
              id="staff-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Nombre y apellido"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="staff-email-local">Correo corporativo</Label>
            <div className="flex items-stretch overflow-hidden rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring">
              <Input
                id="staff-email-local"
                type="text"
                value={emailLocal}
                onChange={(event) =>
                  setEmailLocal(normalizeStaffEmailLocalPart(event.target.value))
                }
                placeholder="nombre.apellido"
                autoComplete="off"
                className="border-0 shadow-none focus-visible:ring-0 rounded-none"
                aria-describedby="staff-email-domain"
              />
              <span
                id="staff-email-domain"
                className="flex items-center bg-muted px-3 text-sm text-muted-foreground whitespace-nowrap border-l border-input"
              >
                {STAFF_EMAIL_DOMAIN}
              </span>
            </div>
            {emailLocal.length > 0 && !emailIsValid && (
              <p className="text-xs text-destructive">
                Solo letras, números y . _ + - (sin espacios).
              </p>
            )}
            {emailIsValid && (
              <p className="text-xs text-muted-foreground font-mono">{email}</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="staff-position">Cargo o área (opcional)</Label>
            <Input
              id="staff-position"
              value={position}
              onChange={(event) => setPosition(event.target.value)}
              placeholder="Soporte, TI, Administración…"
            />
          </div>

          <div className="grid gap-2">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="staff-password">Contraseña inicial</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleGeneratePassword}
              >
                <KeyRound className="h-3.5 w-3.5 mr-1.5" />
                Generar segura
              </Button>
            </div>
            <div className="relative">
              <Input
                id="staff-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                className="pr-20"
              />
              <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
                <button
                  type="button"
                  onClick={() => void handleCopyPassword()}
                  disabled={!password}
                  className="p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-40"
                  aria-label="Copiar contraseña"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowPassword((previous) => !previous)}
                  className="p-1.5 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
            {password.length > 0 && !passwordIsValid && (
              <p className="text-xs text-destructive">
                Mínimo {MIN_PASSWORD_LENGTH} caracteres.
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Generá una clave o escribí/modificá la que quieras. Comunícasela por un
              canal seguro.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={provision.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {provision.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Crear cuenta
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
